'use client';

import { msg, msgf } from '@/services/isomorphic/I18NService';
import { useMemo } from 'react';
import { useLocale } from './useLocale';

/** The type of the {@link msg} function. */
export type MsgFn = typeof msg;
/** The type of the {@link msgf} function. */
export type MsgfFn = typeof msgf;

/**
 * A hook that gets msg and msgf functions with the current locale using the
 * useLocale hook.
 * @returns The msg and msgf functions with the current locale.
 */
export function useLocaleMessage(): [MsgFn, MsgfFn] {
  // Get the locale however it can be accessed.
  const locale = useLocale();

  // Assemble functionally similar methods to represent the msg and msgf functions
  // but with the locale included.
  return useMemo(() => {
    const msgL = (msgID: Parameters<MsgFn>[0]): ReturnType<MsgFn> =>
      msg(msgID, locale);

    const msgfL = (
      msgID: Parameters<MsgfFn>[0],
      formatOpts: Parameters<MsgfFn>[1]
    ): ReturnType<MsgfFn> => msgf(msgID, formatOpts, locale);

    return [msgL as MsgFn, msgfL as MsgfFn];
  }, [locale]);
}
